import { ApolloError } from '@apollo/client';
import * as React from 'react';
import useV2Query from 'utils/useV2Query';
import { GET_LEDGER_LIST } from '../graphql/queries';
import { GetLedgerList, LedgerAccount } from '../types';
import { filterAndGroupLedgersByCategory } from '../utils/ledgerDisplay';
import { useGeneralLedger } from './useGeneralLedger';
import { useClient } from 'bk-web-app/ClientContext';
import { defaultMultiSort, defaultMoneyInTransitSort } from '../utils/sorting';

type UseLedgerListResult = {
  ledgers: LedgerAccount[];
  grouped: Record<string, LedgerAccount[]>;
  ledgerIdToIndexMap: Record<string, number>;
  loading: boolean;
  qualityIssuesSummaryLoading: boolean;
  error: ApolloError | undefined;
  qualityIssuesSummaryError: ApolloError | undefined;
};

export const useLedgerList = (): UseLedgerListResult => {
  const { showAccounts, dateRange, search } = useGeneralLedger();
  const client = useClient();

  const { data, loading, error } = useV2Query<GetLedgerList>(GET_LEDGER_LIST, {
    variables: {
      clientId: client?.id,
      startDate: dateRange?.startDate,
      endDate: dateRange?.endDate,
      query: search,
      ledgerAccountCategoryIds: ['1', '3', '5', '6', '7', '8'],
      multiSort: defaultMultiSort,
      moneyInTransitSort: defaultMoneyInTransitSort,
    },
  });

  const { ledgers, grouped, ledgerIdToIndexMap } = React.useMemo(
    () =>
      filterAndGroupLedgersByCategory(
        data || { ledgerAccountsByClient: [] },
        showAccounts,
        search,
        undefined,
      ),
    [data, showAccounts, search],
  );

  return {
    ledgers,
    grouped,
    ledgerIdToIndexMap,
    loading,
    error,
    qualityIssuesSummaryLoading: false,
    qualityIssuesSummaryError: undefined,
  };
};
